import dayjs from 'dayjs'
import { getTranslate } from 'legacy-src/utilities/i18n'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { toast } from 'react-toastify'

import useEvents from '@/legacy-src/hooks/useEvents'
import useUser from '@/legacy-src/hooks/useUser'
import { USER_TYPE } from '@/legacy-src/redux/UserRedux'
import { getAuthenticatedAppHref } from '@/legacy-src/utilities/getAuthenticatedAppHref'

import { useApis } from '../../../contexts/api-context'
import { EventError } from '../../../types/ReponseErrorEnums'
import notifyMobileApp, { NotifyAppMessages } from '../../../utilities/notifyMobileApp'

type Values = {
  readonly newStartDate: string
  readonly eventId: number
  readonly note?: string
}

const useRebookEvent = () => {
  const router = useRouter()
  const { api } = useApis()
  const { t } = getTranslate()
  const [isLoading, setIsLoading] = useState(false)
  const { user } = useUser()
  const { events } = useEvents()
  const { query } = router

  const isRebookingInProgress = query.eventId

  const originalEventData = events?.find((event) => event.id === Number(query.eventId))

  const cancelRebooking = () => {
    const { eventId, ...routerQuery } = router.query
    router.replace({
      query: { ...routerQuery },
    })
  }

  const rebookEvent = async (values: Values) => {
    const newDate = dayjs(values.newStartDate).format('DD. MM. YYYY')
    const data = {
      newStartDate: values.newStartDate,
      ...(values.note && { note: values.note }),
    }

    try {
      setIsLoading(true)
      await api.changeEventDatetime(values.eventId, data)
      notifyMobileApp(NotifyAppMessages.EVENT_DATE_CHANGE_SUCCESS)
      toast.success<string>(t('component.toaster.rebookingSuccess', { date: newDate }))

      const url = getAuthenticatedAppHref(user.type === USER_TYPE.CLIENT)
      router.push({ pathname: url })
    } catch (error: any) {
      if (error?.status === 400 && error?.data === EventError.isInThePast) {
        toast.error<string>(t('errors.eventPast'))
      } else if (error?.status === 400 && error?.data === EventError.dateOverlap) {
        toast.error<string>(t('errors.eventOverlap'))
      } else if (error?.status === 400 && error?.data === EventError.noAvailableSubscriptionEventsInMonth) {
        toast.error<string>(t('errors.noAvailableSubscriptionEventsInMonth'))
      } else {
        toast.error<string>(t('errors.defaultError'))
      }
    } finally {
      setIsLoading(false)
    }
  }
  return {
    isRebookingInProgress,
    originalEventData,
    cancelRebooking,
    rebookEvent,
    isLoading,
  }
}

export default useRebookEvent
